import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { activityActionLabel } from "@/lib/activity-actions";
import { useAdminUser } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { roleLabel } from "@/lib/roles";

interface Props {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserDetailDialog({ userId, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const { data: user, isLoading, isError } = useAdminUser(open ? userId : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("users.detailTitle")}</DialogTitle>
        </DialogHeader>
        {isError ? (
          <p className="text-destructive py-8 text-center text-sm">{t("users.detailLoadError")}</p>
        ) : isLoading || !user ? (
          <div className="flex justify-center py-8">
            <Loader2 className="text-muted h-6 w-6 animate-spin" aria-label={t("common.loading")} />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm max-sm:grid-cols-[auto_minmax(0,1fr)] max-sm:gap-x-4">
              <p className="text-muted">{t("users.name")}</p>
              <p>{`${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || "—"}</p>
              <p className="text-muted">{t("settings.email")}</p>
              <p className="break-all">{user.email}</p>
              <p className="text-muted">{t("settings.role")}</p>
              <p>{roleLabel(user.role)}</p>
              <p className="text-muted">{t("users.joined")}</p>
              <p>{formatDate(user.created_at)}</p>
              <p className="text-muted">{t("users.updated")}</p>
              <p>{formatDate(user.updated_at)}</p>
              <p className="text-muted">{t("users.lastSignIn")}</p>
              <p>{formatDateTime(user.last_sign_in_at, t("common.never"))}</p>
            </div>
            {user.activity_logs.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-medium">{t("users.recentActivity")}</h3>
                <div className="scrollbar-themed max-h-48 space-y-1 overflow-y-auto">
                  {user.activity_logs.map((entry) => (
                    <div key={entry.id} className="flex justify-between text-xs max-sm:gap-3">
                      <span className="min-w-0 max-sm:wrap-anywhere">
                        <span className="font-medium" title={entry.action}>
                          {activityActionLabel(entry.action)}
                        </span>
                        {entry.detail && <span className="text-muted"> — {entry.detail}</span>}
                      </span>
                      <span className="text-muted whitespace-nowrap">
                        {formatDateTime(entry.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
